import { FC, useMemo } from 'react';

import { BsFillPencilFill } from 'react-icons/bs';
import { cn } from 'utils/helpers';
import useModal from 'ui/partials/Modal/useModal';
import { Formik } from 'formik';
import { type FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { DateField } from 'ui/components/form';
import { DATA_DATE_FORMAT } from 'utils/types';

import Button from '../Button';

interface PROPS {
  title: string;
  value?: string;
  onSubmit: (
    val: Date | string | undefined | null,
    onSuccess: () => void,
  ) => void;
  isLoading?: boolean;
  disabled?: boolean;
  isRequired?: boolean;
  format: DATA_DATE_FORMAT;
}

const QUICK_EDIT_DATE_SCHEMA = Yup.object({
  isRequired: Yup.boolean().optional().default(false),
  value: Yup.date().optional().nullable(),
});

export type QUICK_EDIT_DATE_FORM = Yup.InferType<typeof QUICK_EDIT_DATE_SCHEMA>;

const QuickEditDate: FC<PROPS> = ({
  isRequired = false,
  title,
  value,
  onSubmit: _onSubmit,
  isLoading,
  disabled,
  format,
}) => {
  const { showModal, hideModal } = useModal();

  const onSubmit = (
    values: QUICK_EDIT_DATE_FORM,
    { setSubmitting }: FormikHelpers<QUICK_EDIT_DATE_FORM>,
  ) => {
    const castValues = QUICK_EDIT_DATE_SCHEMA.cast(values);
    _onSubmit(castValues.value, () => {
      setSubmitting(false);
    });
  };

  const handleOpenEditModal = () => {
    showModal({
      title: `Edit ${title}`,
      content: (
        <Formik<QUICK_EDIT_DATE_FORM>
          enableReinitialize
          validationSchema={QUICK_EDIT_DATE_SCHEMA}
          initialValues={{
            isRequired, //TODO: to be removed in future iterations
            value: value ? new Date(value) : undefined,
          }}
          onSubmit={onSubmit}
        >
          {({
            setFieldValue,
            errors,
            touched,
            isSubmitting,
            values,
            handleSubmit,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <DateField
                  showTimeSelect={format === DATA_DATE_FORMAT.DATETIME}
                  label={title}
                  name="date"
                  clearSelected={() => {
                    setFieldValue('value', null);
                  }}
                  selected={values.value}
                  onChange={(_e, value) => setFieldValue('value', value)}
                  textType={
                    errors.value && touched.value ? 'danger' : 'default'
                  }
                  subText={
                    errors.value && touched.value ? errors.value : undefined
                  }
                  {...(format === DATA_DATE_FORMAT.DATETIME && {
                    dateFormat: 'MM/dd/yyyy hh:mm a',
                  })}
                  required={isRequired} //TODO: to be removed in future iterations
                />
                <div
                  className={cn(
                    'mt-5 pt-5',
                    'flex gap-3 justify-end',
                    'border-t-[0.5px] border-separator-primary',
                  )}
                >
                  <Button
                    onClick={hideModal}
                    type="button"
                    disabled={isSubmitting}
                    label="CANCEL"
                    intent="tertiary"
                    size="sm"
                  />
                  <Button
                    disabled={isSubmitting}
                    label="SAVE"
                    type="submit"
                    size="sm"
                  />
                </div>
              </form>
            );
          }}
        </Formik>
      ),
    });
  };

  const noValue = useMemo(() => {
    return `no ${title} yet`;
  }, [title]);

  return (
    <div className="border-b-[0.5px] border-b-separator-primary py-[14px]">
      <p className="text-sm secondary text-content">{title}</p>
      <div className="flex items-center gap-1 text-sm group">
        <div
          className={cn(
            !value?.length && 'text-disabled italic lowercase',
            'flex gap-1 flex-wrap',
          )}
        >
          {value ?? noValue}
        </div>
        <button
          disabled={isLoading}
          type="button"
          className={cn(
            !disabled ? 'block' : 'hidden',
            'group-hover:block hidden',
          )}
          onClick={handleOpenEditModal}
        >
          <BsFillPencilFill className="w-3 h-3 text-secondary-base" />
        </button>
      </div>
    </div>
  );
};

export default QuickEditDate;
